import { useMemo, useState } from 'react';
import { Plus, MessageSquare, History, Search, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Conversation } from '../types';

interface ConversationListProps {
  conversations: Conversation[];
  activeId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  onDelete?: (id: number) => void;
  isLoading?: boolean;
  createDisabled?: boolean;
  isDeleting?: boolean;
}

function formatRelativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  isLoading = false,
  createDisabled = false,
  isDeleting = false,
}: ConversationListProps) {
  const [search, setSearch] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const filteredConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((conv) => {
      const title = conv.title.toLowerCase();
      const lastMessage = (conv.lastMessage ?? '').toLowerCase();
      return title.includes(query) || lastMessage.includes(query);
    });
  }, [conversations, search]);

  const pendingDelete =
    pendingDeleteId != null ? conversations.find((c) => c.id === pendingDeleteId) : undefined;

  return (
    <div className="flex h-full min-w-0 flex-col border-r bg-muted/30">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-muted-foreground">
          <History className="h-4 w-4 shrink-0" />
          <span className="break-words">History</span>
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onNew}
          disabled={createDisabled || isLoading}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="border-b px-2 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search history"
            className="h-8 pl-8 text-sm"
            aria-label="Search conversation history"
          />
        </div>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-1 p-2">
          {conversations.length === 0 ? (
            <p className="px-2 py-4 text-center text-xs text-muted-foreground">
              No conversations yet
            </p>
          ) : filteredConversations.length === 0 ? (
            <p className="px-2 py-4 text-center text-xs text-muted-foreground">
              No conversations match your search
            </p>
          ) : (
            filteredConversations.map((conv) => {
              const when = formatRelativeTime(conv.timestamp);
              const countLabel =
                conv.messageCount != null && conv.messageCount > 0
                  ? `${conv.messageCount} message${conv.messageCount !== 1 ? 's' : ''}`
                  : conv.lastMessage || 'No messages';

              return (
                <div
                  key={conv.id}
                  className={cn(
                    'group flex w-full items-start gap-1 rounded-lg p-1 transition-colors',
                    activeId === conv.id ? 'bg-accent' : 'hover:bg-accent/50'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(conv.id)}
                    className="flex min-w-0 flex-1 items-start gap-2 rounded-md p-1.5 text-left"
                  >
                    <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-medium leading-snug">{conv.title}</p>
                      <p
                        className="mt-0.5 break-words text-xs text-muted-foreground"
                        title={conv.timestamp.toLocaleString()}
                      >
                        {when}
                        <span> · {countLabel}</span>
                      </p>
                    </div>
                    {conv.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </button>
                  {onDelete && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="mt-0.5 h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      title="Delete conversation"
                      aria-label={`Delete ${conv.title}`}
                      disabled={isDeleting}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDeleteId(conv.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      <Dialog
        open={pendingDeleteId != null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete conversation?</DialogTitle>
            <DialogDescription>
              {pendingDelete
                ? `"${pendingDelete.title}" will be removed from your history. You can no longer open or continue it.`
                : 'This conversation will be removed from your history.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDeleteId(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting || pendingDeleteId == null}
              onClick={() => {
                if (pendingDeleteId == null || !onDelete) return;
                onDelete(pendingDeleteId);
                setPendingDeleteId(null);
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
